/**
 * Raised when topic data read back from the browser's Origin Private File System
 * (OPFS) cache is unusable.
 *
 * Cache entries are validated by size, never by content, so an entry of the right
 * length holding the wrong bytes reads as valid. The read then fails deep in a format
 * parser, with messages like "6 bytes remaining in message index section" that tell
 * the user nothing. Readers evict the offending entry and convert the failure into a
 * `CacheReadError`, so the panel can report a cache problem and offer a retry that
 * re-downloads.
 *
 * The `message` is diagnostic and goes to Sentry. User-facing copy lives in the
 * component that renders the error, the only place that knows whether a retry control
 * is on screen.
 */
export class CacheReadError extends Error {
  /**
   * The discriminator is `name` rather than the class: readers run in a web worker,
   * and errors reach the main thread through `SerializableError`, which rebuilds them
   * as plain `Error`s carrying the original `name`. `instanceof` is therefore false on
   * the receiving side.
   */
  static readonly ERROR_NAME = "CacheReadError";

  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = CacheReadError.ERROR_NAME;
  }
}

/**
 * True for a `CacheReadError` raised anywhere, including one round-tripped through a
 * worker boundary. Such a value is no longer an instance of the class, so the
 * narrowing stops at `Error`.
 */
export function isCacheReadError(error: unknown): error is Error {
  return error instanceof Error && error.name === CacheReadError.ERROR_NAME;
}
